import React, { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import styled from 'styled-components';
import Fuse from 'fuse.js';

import Image, { UserProfilePhoto, UserProfilePhotoMissing } from '../core/Image';
import Search, { IUSearchItem, USearchItemField } from '../cache/Search';
import ContactHelper from '../core/libs/ContactHelper';
import ContactsSpinner from './ContactsSpinner';
import { useContactsContext } from '../contexts/ContactsContext';
import ClickOutside from './ClickOutside';
import { ListFullName, SupplementalInfo } from './styled/ContactsList';

import { TAppContact } from 'TProtocol/common/models';
import FuseResult = Fuse.FuseResult;
import FuseResultMatch = Fuse.FuseResultMatch;
import { ProgressBar } from 'react-bootstrap';

const MINIMUM_QUERY_LENGTH = 2;

interface SearchResult {
  contact: TAppContact,
  matches: readonly FuseResultMatch[] | undefined
}

let lastRequest = 0;

const SearchBar = () => {
  const contactsContext = useContactsContext();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    if (query.length < MINIMUM_QUERY_LENGTH) {
      setResults([]);
    } else {
      executeSearch(query);
    }
  }, [query]);

  useEffect(() => {
    if (contactsContext.searchLoadProgress === 100) {
      executeSearch(query);
    }
  }, [contactsContext.searchLoadProgress])

  // set a timeout so text is changed before search is executed
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQuery(event.target.value);
  };

  const executeSearch = debounce(async (query: string) => {
    if (query.length >= MINIMUM_QUERY_LENGTH) {
      const results: FuseResult<IUSearchItem>[] = Search.getContactsForQuery(query);
      const contacts: SearchResult[] = [];
      for (const result of results) {
        // Always make sure the most recent search is the one we process.
        const thisRequest = ++lastRequest;
        const contact = await contactsContext.getContact(result.item?.[USearchItemField.contactId]);
        if (thisRequest !== lastRequest) {
          // Another search has happened, abort.
          return;
        }
        if (contact !== undefined) {
          contacts.push({ contact, matches: result.matches });
        }
      }
      setResults(contacts);
      setLoading(false);
    }
  }, 150);

  const onClick = (contact: TAppContact) => () => {
    hideResults();
    contactsContext.setSelectedContact(contact);
  };

  const hideResults = () => {
    setQuery('');
    setResults([]);
  };

  const contactNameIsIncludedInHighlight = (contact: TAppContact, searchQuery: string) => {
    return ContactHelper.getName(contact).toLowerCase().includes(searchQuery.toLowerCase());
  };

  // If we are not highlighting part of their contact profile, we want to display the default 
  // supplemental field based on what contact information we have. This function uses a ranked list of fields
  // and returns the value of the first one that has data.
  const getDefaultSupplementalText = (contact: TAppContact) => {
    if (contact.primaryProfessionalInfo && contact.primaryProfessionalInfo.companyName) {
      return contact.primaryProfessionalInfo?.companyName;
    }
    if (contact.personalEmail) {
      return contact.personalEmail?.displayEmail;
    }
    if (contact.professionalEmail) {
      return contact.professionalEmail?.displayEmail;
    }
    if (contact.personalMobile) {
      return contact.personalMobile?.displayNumber;
    }
    if (contact.personalLandline) {
      return contact.personalLandline?.displayNumber;
    }
    if (contact.personalAddress && contact.personalAddress.address1) {
      return contact.personalAddress.address1;
    }
    return '';
  };

  const getHighlightedText = (rawText: string) => {
    // const highlightStart = rawText.toLowerCase().indexOf(searchQuery.toLowerCase());
    // const highlightEnd = highlightStart + searchQuery.length;
    // return { __html: rawText.slice(0, highlightStart) + "<span class='highlight'>" + rawText.slice(highlightStart, highlightEnd) + "</span>" + rawText.slice(highlightEnd) };
    return { __html: rawText };
  };

  const getHighlightedSupplementalText = (contact: TAppContact, matchInfo: readonly FuseResultMatch[] | undefined,
    searchQuery: string) => {
    if (contact && matchInfo && matchInfo[0] && matchInfo[0].indices && !contactNameIsIncludedInHighlight(contact,
      searchQuery)) {
      const rawText = matchInfo[0].value ?? '';
      return getHighlightedText(rawText);
    }
    return { __html: getDefaultSupplementalText(contact) ?? '' };
  };

  const getHighlightedPrimaryText = (contact: TAppContact, matchInfo: readonly FuseResultMatch[] | undefined,
    searchQuery: string) => {
    if (contactNameIsIncludedInHighlight(contact,
      searchQuery) && contact && matchInfo && matchInfo[0] && matchInfo[0].indices) {
      const rawText = ContactHelper.getName(contact);
      return getHighlightedText(rawText);
    }
    return { __html: ContactHelper.getName(contact) };
  };

  const progressBar = (contactsContext.searchLoadProgress > 0 && contactsContext.searchLoadProgress < 100) ?
    <SearchBarProgressBar animated variant="info" min={0} max={100} now={contactsContext.searchLoadProgress}/> : null;

  let message: ReactNode = null;
  if (contactsContext.searchLoadProgress > 0 && contactsContext.searchLoadProgress < 100) {
    message = <SearchBarResults>Indexing, please wait...</SearchBarResults>;
  } else if (!contactsContext.contactsLoaded) {
    message = <SearchBarResults>Contacts loading, please wait...</SearchBarResults>
  } else if (results.length === 0 && query.length >= MINIMUM_QUERY_LENGTH && !loading) {
    message = <SearchBarResults>No results found.</SearchBarResults>
  } else if (loading) {
    message = <SearchBarResults><span className="mt-2 mx-auto"><ContactsSpinner/></span></SearchBarResults>;
  }

  return (
    <SearchBarWrapper>
      <SearchBarDiv>
        <ClickOutside onClickOutside={hideResults}>
          <SearchBarQuery>
            <SearchBarInput className="form-control" name="contacts-search-query" onChange={onChange} value={query}
                            placeholder="Search for people"/>
            {query.length >= MINIMUM_QUERY_LENGTH ? <SearchBarContainer>
              <div>{progressBar}</div>
              {!loading && results.map((searchResult) => {
                const contact = searchResult.contact;
                const matchInfo = searchResult.matches;
                return <SearchBarResults key={contact.contactId} onClick={onClick(contact)}>
                  <Image src={contact.photoUrl ?? ''} showSpinner={false}/>
                  <div>
                    <ListFullName
                      dangerouslySetInnerHTML={getHighlightedPrimaryText(contact, matchInfo, query)}></ListFullName>
                    <SupplementalInfo dangerouslySetInnerHTML={getHighlightedSupplementalText(contact, matchInfo,
                      query)}></SupplementalInfo>
                  </div>
                </SearchBarResults>;
              })}
              {message}
            </SearchBarContainer> : null}
          </SearchBarQuery>
        </ClickOutside>
      </SearchBarDiv>
    </SearchBarWrapper>
  );

};

const SearchBarWrapper = styled.div`
  margin: 16px 0;
  max-width: 800px;
`;

const SearchBarDiv = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SearchBarInput = styled.input`
  border-radius: 10px;
  height: 40px;
  border: solid 1px #009bdf;

`;

const SearchBarContainer = styled.div`
  margin-top: -7px;
  position: absolute;
  width: 100%;
  overflow-y: scroll;
  max-height: 450px;
  border-bottom: solid 1px #eee;
`;

const SearchBarQuery = styled.div`
  position: relative;
  flex-grow: 1;
`;

const SearchBarResults = styled.div`
  padding: 10px 10px;
  border: solid 1px #eee;
  border-top: none;
  border-bottom: none;
  background-color: #fff;
  z-index: 10;
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    background-color: #fafafa;
  }

  ${UserProfilePhoto}, ${UserProfilePhotoMissing} {
    width: 32px;
    height: 32px;
    min-width: 32px;
  }

  ${ListFullName} {
    margin-top: 5px;
  }
`;

const SearchBarProgressBar = styled(ProgressBar)`
  height: 0.5rem;
`

// const Highlight = styled.span`
//   background-color: rgba(0, 155, 223, 0.15);
//   font-weight: 600;
// `;

export default SearchBar;
